<template>
  <!-- Card 17trt6jfxt2: chamada sugerida ao fim da aula. Já vem preenchida com
       quem entrou pela plataforma; o professor ajusta, confirma ou deixa para
       depois. Sem confirmação em 48 horas a presença de quem entrou passa a
       valer sozinha, e ele ainda pode alterar na página de presença. -->
  <v-dialog
    v-model="open"
    max-width="560"
    scrollable
    persistent
    @keydown.esc="later"
  >
    <v-card v-if="!lesson && loading" class="pa-card pa-loading">
      <v-progress-circular indeterminate color="#A826E1" size="32" />
    </v-card>
    <v-card v-else-if="lesson" class="pa-card">
      <div class="pa-head">
        <div class="pa-icon">
          <v-icon size="28" color="#A826E1">mdi-account-check-outline</v-icon>
        </div>
        <div>
          <div class="pa-title">{{ $t("attendancePrompt.title") }}</div>
          <div class="pa-sub">
            {{ lesson.title }} · {{ dateLabel }} · {{ lesson.startsAt }}
            <template v-if="lessons.length > 1">
              ·
              {{ $t("attendancePrompt.remaining", { count: lessons.length }) }}
            </template>
          </div>
        </div>
      </div>

      <p class="pa-text">{{ $t("attendancePrompt.text") }}</p>

      <div class="pa-list">
        <div
          v-for="student in lesson.students"
          :key="student.customerId"
          class="pa-row"
        >
          <div class="pa-row__who">
            <span class="pa-row__name">{{
              student.name || $t("attendancePrompt.student")
            }}</span>
            <span v-if="student.joined" class="pa-row__joined">
              <v-icon size="14" color="#2e7d32">mdi-login-variant</v-icon>
              {{ $t("attendancePrompt.joined") }}
            </span>
            <span v-else class="pa-row__unknown">
              {{ $t("attendancePrompt.noEntry") }}
            </span>
          </div>

          <v-btn-toggle
            v-model="marks[student.customerId]"
            dense
            rounded
            class="pa-row__toggle"
          >
            <v-btn :value="1" small class="pa-present">
              {{ $t("attendancePrompt.present") }}
            </v-btn>
            <v-btn :value="2" small class="pa-absent">
              {{ $t("attendancePrompt.absent") }}
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <p v-if="lesson.autoConfirmAt" class="pa-note">
        {{ $t("attendancePrompt.autoNote") }}
      </p>

      <div class="pa-actions">
        <v-btn text @click="later">{{ $t("attendancePrompt.later") }}</v-btn>
        <v-btn
          depressed
          rounded
          dark
          color="#A826E1"
          :loading="saving"
          :disabled="!hasMarks"
          @click="confirm"
        >
          {{ $t("attendancePrompt.confirm") }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import pitchPartnerApi from "@/shared/pitchPartnerApi";
import {
  ATTENDANCE_OPEN_EVENT,
  ATTENDANCE_REFRESH_EVENT,
  dispatchAttendanceRefresh,
  loadPendingAttendance,
} from "./attendanceEvents";

// Por sessão do navegador: abre sozinho a cada nova entrada, não a cada troca
// de página. O alerta da página inicial abre à vontade.
const SEEN_KEY = "pitch:attendance-prompt-seen";

function readSeen() {
  try {
    return JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeSeen(ids) {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(ids));
  } catch {
    // Sem sessionStorage o aviso só reaparece mais vezes.
  }
}

export default {
  name: "PendingAttendancePrompt",

  data() {
    return {
      lessons: [],
      open: false,
      saving: false,
      loading: false,
      // customerId -> 1 (presente) | 2 (falta) | undefined (sem marcação)
      marks: {},
    };
  },

  computed: {
    lesson() {
      return this.lessons[0] || null;
    },
    hasMarks() {
      return Object.values(this.marks).some(
        (value) => value === 1 || value === 2
      );
    },
    dateLabel() {
      const raw = String(this.lesson?.eventDate || "").slice(0, 10);
      const [year, month, day] = raw.split("-");
      return year ? `${day}/${month}` : "";
    },
  },

  watch: {
    lesson: {
      immediate: true,
      handler() {
        this.fillMarks();
      },
    },
  },

  mounted() {
    this.load(true);
    window.addEventListener(ATTENDANCE_OPEN_EVENT, this.openFromAlert);
    window.addEventListener(ATTENDANCE_REFRESH_EVENT, this.refresh);
  },

  beforeDestroy() {
    window.removeEventListener(ATTENDANCE_OPEN_EVENT, this.openFromAlert);
    window.removeEventListener(ATTENDANCE_REFRESH_EVENT, this.refresh);
  },

  methods: {
    // Quem entrou na janela da aula já vem como presente; os demais ficam sem
    // marcação (nunca viram falta sozinhos).
    fillMarks() {
      const marks = {};
      for (const student of this.lesson?.students || []) {
        if (student.suggestedStatus) {
          marks[student.customerId] = student.suggestedStatus;
        }
      }
      this.marks = marks;
    },

    async load(auto) {
      const lessons = await loadPendingAttendance();
      if (lessons === null) return;
      this.lessons = lessons;
      if (!auto) return;
      const seen = readSeen();
      const unseen = this.lessons.some((item) => !seen.includes(item.eventId));
      this.open = unseen && !!this.lessons.length;
    },

    // Retorna false quando a consulta falhou: nesse caso a janela fica como
    // está, só fecha quando a API confirma que não há mais pendência.
    async refresh() {
      const lessons = await loadPendingAttendance();
      if (lessons === null) return false;
      this.lessons = lessons;
      if (!lessons.length) this.open = false;
      return true;
    },

    // O alerta da página já mostrou que há pendência: abre na hora com o que
    // está em memória e atualiza em segundo plano, sem a sensação de que nada
    // aconteceu enquanto a API responde.
    async openFromAlert() {
      this.open = true;
      this.loading = !this.lessons.length;
      const ok = await this.refresh();
      this.loading = false;
      if (!ok && !this.lessons.length) {
        this.open = false;
        this.$root.$emit("display-alert", {
          text: this.$t("attendancePrompt.loadError"),
          type: 1,
        });
      }
    },

    markSeen() {
      writeSeen(
        Array.from(
          new Set([...readSeen(), ...this.lessons.map((item) => item.eventId)])
        )
      );
    },

    later() {
      this.markSeen();
      this.open = false;
    },

    async confirm() {
      const lesson = this.lesson;
      if (!lesson || this.saving) return;

      const attendances = Object.entries(this.marks)
        .filter(([, status]) => status === 1 || status === 2)
        .map(([customerId, status]) => ({ customerId, status }));
      if (!attendances.length) return;

      this.saving = true;
      try {
        await pitchPartnerApi.saveLessonAttendance(
          lesson.classId,
          lesson.eventId,
          { attendances }
        );
        this.$root.$emit("display-alert", {
          text: this.$t("attendancePrompt.saved"),
          type: 0,
        });
        dispatchAttendanceRefresh();
        await this.refresh();
      } catch {
        this.$root.$emit("display-alert", {
          text: this.$t("attendancePrompt.saveError"),
          type: 1,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pa-card {
  padding: 24px 24px 16px;
  border-radius: 16px !important;
  max-height: 90vh;
  overflow-y: auto;
}

.pa-loading {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.pa-head {
  display: flex;
  gap: 14px;
  align-items: center;
}

.pa-icon {
  flex: 0 0 52px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dk-surface-alt, #f4eafb);
}

.pa-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--dk-ink, #2b2540);
}

.pa-sub {
  font-size: 0.85rem;
  color: var(--dk-muted, #6b6580);
}

.pa-text {
  margin: 14px 0 10px;
  color: var(--dk-ink-soft, #4d4760);
}

.pa-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pa-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--dk-paper, #faf9fc);
  border: 1px solid var(--dk-line, #ece8f3);
}

.pa-row__who {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pa-row__name {
  font-weight: 600;
  color: var(--dk-ink, #2b2540);
}

.pa-row__joined {
  font-size: 0.78rem;
  color: #2e7d32;
}

.pa-row__unknown {
  font-size: 0.78rem;
  color: var(--dk-muted, #6b6580);
}

.pa-row__toggle {
  flex: 0 0 auto;
}

.pa-note {
  margin: 12px 0 0;
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6580);
}

.pa-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
</style>
